/**
 * LES DATES D'UNE SAISON, dans l'onglet Saisons des Référentiels — celles que son code porte (une proposition), ou celles
 * que le commerçant a posées ; un geste pour les corriger, un pour revenir à celles du code.
 *
 * ⚠️ CE QUE L'ÉCRAN MONTRE EST CE QUE L'ÂGE DU STOCK LIT : les dates posées si la saison en a, sinon la règle du cœur
 *    (`seasonDating`), la même que le serveur — une période affichée ici et une autre comptée là-bas ferait mentir la carte.
 * ⚠️ LES DEUX BORNES OU AUCUNE, refusées par la même règle que le producteur (`seasonDatesRefusal`) : un refus se dit avant
 *    l'envoi, dans la langue du compte, au lieu d'une phrase du serveur.
 */
import { useState } from 'react';
import { seasonDating, seasonDatesRefusal, type SeasonDatesRefusal } from '@orgazone/core-catalog';
import type { ReferentialFacetEntry } from '../api';
import { key, t, type Translatable } from '../i18n';
import { rangeLabel } from './stats-bar';

/** `AAAAMMJJ` → `AAAA-MM-JJ`, le format d'un champ de date. */
const isoOf = (ymd: string): string => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6)}`;
/** `AAAA-MM-JJ` → `AAAAMMJJ` ; un champ vide ne date rien. */
const ymdOf = (iso: string): string | null => (iso ? iso.replaceAll('-', '') : null);

/** Les dates posées d'une saison, quand elle en a. */
const posedOf = (row: ReferentialFacetEntry): { from: string; to: string } | null =>
  (row.startDay && row.endDay ? { from: row.startDay, to: row.endDay } : null);

const REFUSALS: Record<SeasonDatesRefusal, Translatable> = {
  incomplete: key('Indiquez le début et la fin, ou aucune des deux.'),
  bad_day: key('Une de ces dates n’existe pas.'),
  reversed: key('La saison finirait avant de commencer.'),
};

/** La période d'une saison, et d'où elle vient : posée, tirée du code, permanente, ou rien. */
export function SeasonDatesLabel({ row, busy, onEdit }: { row: ReferentialFacetEntry; busy: boolean; onEdit: () => void }) {
  const posed = posedOf(row);
  const fromCode = posed ? null : seasonDating(row.code);
  const period = posed ?? (fromCode?.kind === 'dated' ? fromCode : null);
  const origin = posed ? t('corrigée') : fromCode?.kind === 'dated' ? t('d’après le code') : fromCode ? t('permanente') : t('non datée');
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        {period && (
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {rangeLabel(isoOf(period.from), isoOf(period.to))}
          </span>
        )}
        <span style={{ fontSize: 'var(--t-meta)', color: posed ? 'var(--teal-800)' : 'var(--ink-400)', whiteSpace: 'nowrap' }}>{origin}</span>
      </span>
      <button type="button" disabled={busy} onClick={onEdit} aria-label={t('Corriger les dates de « {v} »', { v: row.label })}
        style={{
          height: 26, padding: '0 9px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)',
          fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-700)', cursor: busy ? 'default' : 'pointer',
        }}>{t('Dates')}</button>
    </span>
  );
}

/**
 * Le formulaire des dates d'une saison, ouvert sous sa ligne : début et fin préremplis de la période en vigueur.
 * `onWrite` écrit (deux \`null\` rendent la saison à son code) et rend le motif d'un échec, ou \`null\`.
 */
export function SeasonDatesEditor({ row, busy, onWrite, onClose }: {
  row: ReferentialFacetEntry;
  busy: boolean;
  onWrite: (startDay: string | null, endDay: string | null) => Promise<string | null>;
  onClose: () => void;
}) {
  const posed = posedOf(row);
  const fromCode = seasonDating(row.code);
  const start = posed ?? (fromCode?.kind === 'dated' ? fromCode : null);
  const [from, setFrom] = useState(start ? isoOf(start.from) : '');
  const [to, setTo] = useState(start ? isoOf(start.to) : '');
  const [error, setError] = useState<string | null>(null);

  const save = async (startDay: string | null, endDay: string | null) => {
    const refusal = seasonDatesRefusal(startDay, endDay);
    if (refusal) { setError(t(REFUSALS[refusal])); return; }
    const failure = await onWrite(startDay, endDay);
    if (failure) setError(failure); else onClose();
  };

  const field = { height: 32, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-body)', color: 'var(--ink-900)' };
  const button = (primary: boolean) => ({
    height: 32, padding: '0 12px', border: primary ? 'none' : '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
    background: primary ? 'var(--ink-900)' : 'var(--paper)', color: primary ? 'var(--paper)' : 'var(--ink-700)',
    fontSize: 'var(--t-body)', fontWeight: 600, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' as const,
  });
  return (
    <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0 14px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="date" value={from} aria-label={t('Début de la saison « {v} »', { v: row.label })}
          onChange={(e) => { setFrom(e.target.value); setError(null); }} style={field} />
        <span aria-hidden style={{ color: 'var(--ink-400)' }}>→</span>
        <input type="date" value={to} aria-label={t('Fin de la saison « {v} »', { v: row.label })}
          onChange={(e) => { setTo(e.target.value); setError(null); }} style={field} />
        <button type="button" disabled={busy} onClick={() => void save(ymdOf(from), ymdOf(to))} style={button(true)}>{t('Enregistrer')}</button>
        <button type="button" disabled={busy} onClick={onClose} style={button(false)}>{t('Annuler')}</button>
        {posed && (
          <button type="button" disabled={busy} onClick={() => void save(null, null)} style={button(false)}>{t('Reprendre les dates du code')}</button>
        )}
      </div>
      {error && <span role="alert" style={{ fontSize: 'var(--t-meta)', color: 'var(--rose-fg)' }}>{error}</span>}
      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
        {fromCode?.kind === 'dated'
          ? t('Sans dates posées, la saison prend celles de son code : {periode}.', { periode: rangeLabel(isoOf(fromCode.from), isoOf(fromCode.to)) })
          : fromCode
            ? t('Son code la dit permanente : sans dates posées, l’âge du stock la compte « permanente ».')
            : t('Son code ne dit pas ses dates : sans dates posées, l’âge du stock la compte « non datée ».')}
      </span>
    </div>
  );
}
